import axiosInstance from '@/lib/axios'
import { cache } from 'react'
import { createSearchParams } from '@/lib/utils'
import { MetaData } from '@/types/meta'
import {
  UsersListResponse,
  UsersFilterParams,
  UserType,
  CreateUserParams,
  UpdateUserParams,
  SendEmailVerificationParams
} from '@/types/user'
import {
  I18nMessagesResponse,
  I18nMessagesFilterParams,
  I18nMessageType,
  CreateI18nMessageParams,
  UpdateI18nMessageParams
} from '@/types/i18n-message'
import {
  CreateRouteParamParams,
  RouteParamsFilterParams,
  RouteParamsResponse,
  RouteParamType,
  UpdateRouteParamParams
} from '@/types/route-param'
import {
  LocalesListResponse,
  LocalesFilterParams,
  LocaleType,
  CreateLocaleParams,
  UpdateLocaleParams
} from '@/types/locale'
import { FilesListResponse, FilesFilterParams, FileType, CreateFileParams, UploadProgressCallback } from '@/types/file'
import { BulkIdParams, BulkDeleteResponse } from '@/types/general'
import { CitiesListResponse, CountriesListResponse, StatesListResponse } from '@/types/country'
import {
  CreateVehicleGroupParams,
  VehicleGroupsFilterParams,
  VehicleGroupsListResponse,
  VehicleGroupType,
  UpdateVehicleGroupParams
} from '@/types/vehicle-group'
import { BrandsListResponse, BrandsFilterParams, BrandType, CreateBrandParams, UpdateBrandParams } from '@/types/brand'
import {
  CurrenciesFilterParams,
  CurrenciesListResponse,
  CurrencyType,
  CreateCurrencyParams,
  UpdateCurrencyParams
} from '@/types/currency'
import { ExchangeRatesFilterParams, ExchangeRatesListResponse, ExchangeRateSyncResponse } from '@/types/exchange-rate'
import {
  CategoriesListResponse,
  CategoriesFilterParams,
  CategoriesTreeResponse,
  CategoryType,
  CreateCategoryParams,
  UpdateCategoryParams
} from '@/types/category'
import {
  PropertiesResponse,
  PropertiesFilterParams,
  PropertyType,
  CreatePropertyParams,
  UpdatePropertyParams
} from '@/types/property'
import {
  CreateProductParams,
  ProductsFilterParams,
  ProductsListResponse,
  ProductType,
  UpdateProductParams
} from '@/types/product'
import {
  BlogPostsListResponse,
  BlogPostsFilterParams,
  BlogPostType,
  CreateBlogPostParams,
  UpdateBlogPostParams
} from '@/types/blog-post'
import { PagesListResponse, PagesFilterParams, PageType, CreatePageParams, UpdatePageParams } from '@/types/page'
import {
  ProductionStagesListResponse,
  ProductionStagesFilterParams,
  ProductionStageType,
  CreateProductionStageParams,
  UpdateProductionStageParams
} from '@/types/production-stage'
import { EventsListResponse, EventsFilterParams, EventType, CreateEventParams, UpdateEventParams } from '@/types/event'
import config from '@/config'
import { SortOrderEnum } from '@/enums'
import {
  CreatePhotoGalleryParams,
  PhotoGalleriesFilterParams,
  PhotoGalleriesListResponse,
  PhotoGalleryType,
  UpdatePhotoGalleryParams
} from '@/types/photo-gallery'
import { CreateVideoParams, VideosFilterParams, VideosListResponse, VideoType, UpdateVideoParams } from '@/types/video'
import { CreateSlideParams, SlidesFilterParams, SlidesListResponse, SlideType, UpdateSlideParams } from '@/types/slide'
import {
  CreateCatalogueParams,
  CataloguesFilterParams,
  CataloguesListResponse,
  CatalogueType,
  UpdateCatalogueParams
} from '@/types/catalogue'
import {
  CreateCertificateParams,
  CertificatesFilterParams,
  CertificatesListResponse,
  CertificateType,
  UpdateCertificateParams
} from '@/types/certificate'
import {
  GroupsListResponse,
  GroupsFilterParams,
  GroupType,
  CreateGroupParams,
  UpdateGroupParams,
  PermissionsListResponse,
  PermissionsFilterParams
} from '@/types/group'
import { CreateModelParams, ModelsFilterParams, ModelsListResponse, ModelType, UpdateModelParams } from '@/types/model'
import {
  CreateProductExcelExportParams,
  ProductExcelExportsFilterParams,
  ProductExcelExportsListResponse,
  ProductExcelExportType
} from '@/types/product-excel-export'
import {
  CreateProductQrExportParams,
  ProductQrExportsFilterParams,
  ProductQrExportsListResponse,
  ProductQrExportType
} from '@/types/product-qr-export'
import {
  CreateProductPdfExportParams,
  ProductPdfExportsFilterParams,
  ProductPdfExportsListResponse,
  ProductPdfExportType
} from '@/types/product-pdf-export'
import {
  CreateProductExcelImportParams,
  ProductExcelImportsFilterParams,
  ProductExcelImportsListResponse,
  ProductExcelImportType
} from '@/types/product-excel-import'
import {
  CreateProductPictureImportParams,
  ProductPictureImportsFilterParams,
  ProductPictureImportsListResponse,
  ProductPictureImportType
} from '@/types/product-picture-import'
import {
  NotificationsListResponse,
  NotificationsFilterParams,
  NotificationType,
  SetReadNotificationParams
} from '@/types/notification'
import {
  ContactUnitsListResponse,
  ContactUnitsFilterParams,
  ContactUnitType,
  CreateContactUnitParams,
  UpdateContactUnitParams
} from '@/types/contact-unit'
import { DashboardResponseType, ChartDataPoint, RevenueDataPoint, OrderStatusPoint } from '@/types/dashboard'
import { AuditLogsFilterParams, AuditLogType, AuditLogsListResponse } from '@/types/audit-log'
import { SearchLogsFilterParams, SearchLogType, SearchLogsListResponse } from '@/types/search-log'
import {
  ContactFormMessagesFilterParams,
  ContactFormMessageType,
  ContactFormMessagesListResponse
} from '@/types/contact-message'
import { JobApplicationsFilterParams, JobApplicationType, JobApplicationsListResponse } from '@/types/job-application'
import {
  AddressesFilterParams,
  AddressesListResponse,
  AddressType,
  CreateAddressParams,
  UpdateAddressParams
} from '@/types/address'
import {
  BackupsFilterParams,
  BackupsListResponse,
  BackupScheduleType,
  BackupType,
  CreateBackupScheduleParams,
  UpdateBackupScheduleParams,
  CreateBackupParams,
  BackupRunsFilterParams,
  BackupRunsListResponse
} from '@/types/backup'
import {
  OrdersFilterParams,
  OrderType,
  OrdersListResponse,
  UpdateOrderParams,
  UpdateOrderStatusParams,
  AdminCreateGuestOrderParams,
  AdminCreateRegisteredOrderParams,
  AddOrderItemParams,
  UpdateOrderItemParams,
  OrderNoteParams,
  OrderNoteType
} from '@/types/order'

export const fetchMetaData = cache(async (): Promise<MetaData> => {
  const url = `${config.API_BASE_URL}/shared/essential/meta`
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    cache: 'no-store'
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch metadata: ${response.status}`)
  }
  return response.json()
})

// Dashboard
export const fetchDashboard = async (): Promise<DashboardResponseType> => {
  const { data } = await axiosInstance.get('/admin/dashboard')
  return data
}

export const fetchNewUsersChart = async (range: string): Promise<ChartDataPoint[]> => {
  const { data } = await axiosInstance.get(`/admin/dashboard/charts/new-users?range=${range}`)
  return data
}

export const fetchNewOrdersChart = async (range: string): Promise<ChartDataPoint[]> => {
  const { data } = await axiosInstance.get(`/admin/dashboard/charts/new-orders?range=${range}`)
  return data
}

export const fetchRevenueChart = async (range: string): Promise<RevenueDataPoint[]> => {
  const { data } = await axiosInstance.get(`/admin/dashboard/charts/revenue?range=${range}`)
  return data
}

export const fetchOrderStatusChart = async (range: string): Promise<OrderStatusPoint[]> => {
  const { data } = await axiosInstance.get(`/admin/dashboard/charts/order-status?range=${range}`)
  return data
}

export const fetchUsers = async (params: UsersFilterParams): Promise<UsersListResponse> => {
  const { data } = await axiosInstance.get(`/admin/users?${createSearchParams(params).toString()}`)
  return data
}

export const fetchUser = async (userId: string): Promise<UserType> => {
  const { data } = await axiosInstance.get(`/admin/users/${userId}`)
  return data
}

export const createUser = async (params: CreateUserParams): Promise<UserType> => {
  const { data } = await axiosInstance.post('/admin/users', params)
  return data
}

export const updateUser = async (userId: string, params: UpdateUserParams): Promise<UserType> => {
  const { data } = await axiosInstance.patch(`/admin/users/${userId}`, params)
  return data
}

export const deleteUser = async (userId: string): Promise<void> => await axiosInstance.delete(`/admin/users/${userId}`)

export const bulkDeleteUsers = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/users', {
    data: params
  })
  return data
}

export const sendEmailVerification = async (userId: string, params: SendEmailVerificationParams): Promise<void> =>
  axiosInstance.post(`/admin/users/${userId}/send-email-verification`, params)

export const fetchUserAddresses = async (
  userId: string,
  params: AddressesFilterParams = {}
): Promise<AddressesListResponse> => {
  const searchParams = createSearchParams(params)

  if (params.size !== undefined) {
    searchParams.delete('size')
    searchParams.append('Size', params.size.toString())
  }

  const { data } = await axiosInstance.get(`/admin/users/${userId}/addresses?${searchParams.toString()}`)
  return data
}

export const fetchUserAddress = async (userId: string, addressId: string): Promise<AddressType> => {
  const { data } = await axiosInstance.get(`/admin/users/${userId}/addresses/${addressId}`)
  return data
}

export const createUserAddress = async (userId: string, params: CreateAddressParams): Promise<AddressType> => {
  const { data } = await axiosInstance.post(`/admin/users/${userId}/addresses`, params)
  return data
}

export const updateUserAddress = async (
  userId: string,
  addressId: string,
  params: UpdateAddressParams
): Promise<AddressType> => {
  const { data } = await axiosInstance.patch(`/admin/users/${userId}/addresses/${addressId}`, params)
  return data
}

export const deleteUserAddress = async (userId: string, addressId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/users/${userId}/addresses/${addressId}`)

export const bulkDeleteUserAddresses = async (userId: string, params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete(`/admin/users/${userId}/addresses`, {
    data: params
  })
  return data
}

export const setPrimaryUserAddress = async (userId: string, addressId: string): Promise<AddressType> => {
  const { data } = await axiosInstance.patch(`/admin/users/${userId}/addresses/${addressId}/set-primary`)
  return data
}

export const fetchI18nMessages = async (params: I18nMessagesFilterParams): Promise<I18nMessagesResponse> => {
  const { data } = await axiosInstance.get(`/admin/i18n-messages?${createSearchParams(params).toString()}`)
  return data
}

export const fetchI18nMessage = async (messageId: string): Promise<I18nMessageType> => {
  const { data } = await axiosInstance.get(`/admin/i18n-messages/${messageId}`)
  return data
}

export const createI18nMessage = async (params: CreateI18nMessageParams): Promise<I18nMessageType> => {
  const { data } = await axiosInstance.post('/admin/i18n-messages', params)
  return data
}

export const updateI18nMessage = async (
  messageId: string,
  params: UpdateI18nMessageParams
): Promise<I18nMessageType> => {
  const { data } = await axiosInstance.patch(`/admin/i18n-messages/${messageId}`, params)
  return data
}

export const deleteI18nMessage = async (messageId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/i18n-messages/${messageId}`)

export const bulkDeleteI18nMessages = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/i18n-messages', {
    data: params
  })
  return data
}

export const fetchRouteParams = async (params: RouteParamsFilterParams): Promise<RouteParamsResponse> => {
  const { data } = await axiosInstance.get(`/admin/route-params?${createSearchParams(params).toString()}`)
  return data
}

export const fetchRouteParam = async (paramId: string): Promise<RouteParamType> => {
  const { data } = await axiosInstance.get(`/admin/route-params/${paramId}`)
  return data
}

export const createRouteParam = async (params: CreateRouteParamParams): Promise<RouteParamType> => {
  const { data } = await axiosInstance.post('/admin/route-params', params)
  return data
}

export const updateRouteParam = async (paramId: string, params: UpdateRouteParamParams): Promise<RouteParamType> => {
  const { data } = await axiosInstance.patch(`/admin/route-params/${paramId}`, params)
  return data
}

export const deleteRouteParam = async (paramId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/route-params/${paramId}`)

export const bulkDeleteRouteParams = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/route-params', {
    data: params
  })
  return data
}

export const fetchLocales = async (params: LocalesFilterParams): Promise<LocalesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/locales?${createSearchParams(params).toString()}`)
  return data
}

export const sortLocale = async (localeId: string, type: SortOrderEnum): Promise<LocaleType> => {
  const { data } = await axiosInstance.patch(`/admin/locales/${localeId}/${type}`)
  return data
}

export const fetchLocale = async (localeId: string): Promise<LocaleType> => {
  const { data } = await axiosInstance.get(`/admin/locales/${localeId}`)
  return data
}

export const createLocale = async (params: CreateLocaleParams): Promise<LocaleType> => {
  const { data } = await axiosInstance.post('/admin/locales', params)
  return data
}

export const updateLocale = async (localeId: string, params: UpdateLocaleParams): Promise<LocaleType> => {
  const { data } = await axiosInstance.patch(`/admin/locales/${localeId}`, params)
  return data
}

export const deleteLocale = async (localeId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/locales/${localeId}`)

export const bulkDeleteLocales = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/locales', {
    data: params
  })
  return data
}

export const fetchFiles = async (params: FilesFilterParams): Promise<FilesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/files?${createSearchParams(params).toString()}`)
  return data
}

export const fetchFile = async (id: string): Promise<FileType> => {
  const { data } = await axiosInstance.get(`/admin/files/${id}`)
  return data
}

export const deleteFile = async (id: string): Promise<void> => await axiosInstance.delete(`/admin/files/${id}`)

export const bulkDeleteFiles = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/files', {
    data: params
  })
  return data
}

export const createFile = async (params: CreateFileParams): Promise<FileType> => {
  const formData = new FormData()
  if (params.file) {
    formData.append('file', params.file)
  }
  if (params.name) {
    formData.append('name', params.name)
  }
  if (params.optimize) {
    formData.append('optimize', params.optimize.toString())
  }
  if (params.accessLevel) {
    formData.append('accessLevel', params.accessLevel)
  }

  const { data } = await axiosInstance.post('/admin/files', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: progressEvent => {
      if (params.onProgress && progressEvent.total) {
        // Upload progress: 0-90% (leave room for backend processing)
        const uploadProgress = Math.round((progressEvent.loaded * 90) / progressEvent.total)
        params.onProgress(uploadProgress)
      }
    }
  })

  return data
}

export const fetchCountries = async (): Promise<CountriesListResponse> => {
  const response = await fetch(`${config.API_BASE_URL}/shared/essential/countries`, {
    headers: {
      'Content-Type': 'application/json'
    },
    cache: 'force-cache' // Cache country data for better performance
  })

  if (!response.ok) {
    throw new Error('Failed to fetch countries')
  }

  return response.json()
}

export const fetchStates = async (countryId: string): Promise<StatesListResponse> => {
  const response = await fetch(`${config.API_BASE_URL}/shared/essential/states/${countryId}`, {
    headers: {
      'Content-Type': 'application/json'
    },
    cache: 'force-cache'
  })

  if (!response.ok) {
    throw new Error('Failed to fetch states')
  }

  return response.json()
}

export const fetchCities = async (stateId: string): Promise<CitiesListResponse> => {
  const response = await fetch(`${config.API_BASE_URL}/shared/essential/cities/${stateId}`, {
    headers: {
      'Content-Type': 'application/json'
    },
    cache: 'force-cache'
  })

  if (!response.ok) {
    throw new Error('Failed to fetch cities')
  }

  return response.json()
}

export const updateFile = async (
  id: string,
  formData: FormData,
  onProgress?: UploadProgressCallback
): Promise<FileType> => {
  const { data } = await axiosInstance.patch(`/admin/files/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: progressEvent => {
      if (onProgress && progressEvent.total) {
        // Upload progress: 0-90% (leave room for backend processing)
        const uploadProgress = Math.round((progressEvent.loaded * 90) / progressEvent.total)
        onProgress(uploadProgress)
      }
    }
  })

  return data
}

export const downloadFile = async (
  id: string,
  onProgress?: UploadProgressCallback,
  abortController?: AbortController
): Promise<Blob> => {
  const response = await axiosInstance.get(`/shared/media/download/${id}`, {
    responseType: 'blob',
    signal: abortController?.signal,
    onDownloadProgress: progressEvent => {
      if (onProgress && progressEvent.total) {
        // Calculate the progress percentage (0-100)
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    }
  })

  return response.data
}

export const fetchVehicleGroups = async (params: VehicleGroupsFilterParams): Promise<VehicleGroupsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/vehicle-groups?${createSearchParams(params).toString()}`)
  return data
}

export const fetchVehicleGroup = async (vehicleGroupId: string): Promise<VehicleGroupType> => {
  const { data } = await axiosInstance.get(`/admin/vehicle-groups/${vehicleGroupId}`)
  return data
}

export const createVehicleGroup = async (params: CreateVehicleGroupParams): Promise<VehicleGroupType> => {
  const { data } = await axiosInstance.post('/admin/vehicle-groups', params)
  return data
}

export const updateVehicleGroup = async (
  vehicleGroupId: string,
  params: UpdateVehicleGroupParams
): Promise<VehicleGroupType> => {
  const { data } = await axiosInstance.patch(`/admin/vehicle-groups/${vehicleGroupId}`, params)
  return data
}

export const sortVehicleGroup = async (vehicleGroupId: string, type: SortOrderEnum): Promise<VehicleGroupType> => {
  const { data } = await axiosInstance.patch(`/admin/vehicle-groups/${vehicleGroupId}/${type}`)
  return data
}

export const deleteVehicleGroup = async (vehicleGroupId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/vehicle-groups/${vehicleGroupId}`)

export const bulkDeleteVehicleGroups = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/vehicle-groups', {
    data: params
  })
  return data
}

export const fetchBrands = async (params: BrandsFilterParams = {}): Promise<BrandsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/brands?${createSearchParams(params).toString()}`)
  return data
}

export const fetchBrand = async (brandId: string): Promise<BrandType> => {
  const { data } = await axiosInstance.get(`/admin/brands/${brandId}`)
  return data
}

export const createBrand = async (params: CreateBrandParams): Promise<BrandType> => {
  const { data } = await axiosInstance.post('/admin/brands', params)
  return data
}

export const updateBrand = async (brandId: string, params: UpdateBrandParams): Promise<BrandType> => {
  const { data } = await axiosInstance.patch(`/admin/brands/${brandId}`, params)
  return data
}

export const deleteBrand = async (brandId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/brands/${brandId}`)

export const bulkDeleteBrands = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/brands', {
    data: params
  })
  return data
}

export const fetchModels = async (params: ModelsFilterParams = {}): Promise<ModelsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/models?${createSearchParams(params).toString()}`)
  return data
}

export const fetchModel = async (modelId: string): Promise<ModelType> => {
  const { data } = await axiosInstance.get(`/admin/models/${modelId}`)
  return data
}

export const createModel = async (params: CreateModelParams): Promise<ModelType> => {
  const { data } = await axiosInstance.post('/admin/models', params)
  return data
}

export const updateModel = async (modelId: string, params: UpdateModelParams): Promise<ModelType> => {
  const { data } = await axiosInstance.patch(`/admin/models/${modelId}`, params)
  return data
}

export const deleteModel = async (modelId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/models/${modelId}`)

export const bulkDeleteModels = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/models', {
    data: params
  })
  return data
}

export const fetchCategories = async (params: CategoriesFilterParams = {}): Promise<CategoriesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/categories?${createSearchParams(params).toString()}`)
  return data
}

export const fetchCategoriesTree = async (): Promise<CategoriesTreeResponse> => {
  const { data } = await axiosInstance.get('/admin/categories/tree')
  return data
}

export const fetchCategory = async (categoryId: string): Promise<CategoryType> => {
  const { data } = await axiosInstance.get(`/admin/categories/${categoryId}`)
  return data
}

export const createCategory = async (params: CreateCategoryParams): Promise<CategoryType> => {
  const { data } = await axiosInstance.post('/admin/categories', params)
  return data
}

export const updateCategory = async (categoryId: string, params: UpdateCategoryParams): Promise<CategoryType> => {
  const { data } = await axiosInstance.patch(`/admin/categories/${categoryId}`, params)
  return data
}

export const deleteCategory = async (categoryId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/categories/${categoryId}`)

export const sortCategory = async (categoryId: string, type: SortOrderEnum): Promise<CategoryType> => {
  const { data } = await axiosInstance.patch(`/admin/categories/${categoryId}/${type}`)
  return data
}

export const bulkDeleteCategories = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/categories', {
    data: params
  })
  return data
}

export const fetchProperties = async (params: PropertiesFilterParams): Promise<PropertiesResponse> => {
  const { data } = await axiosInstance.get(`/admin/properties?${createSearchParams(params).toString()}`)
  return data
}

export const sortProperty = async (propertyId: string, type: SortOrderEnum): Promise<PropertyType> => {
  const { data } = await axiosInstance.patch(`/admin/properties/${propertyId}/${type}`)
  return data
}

export const fetchProperty = async (propertyId: string): Promise<PropertyType> => {
  const { data } = await axiosInstance.get(`/admin/properties/${propertyId}`)
  return data
}

export const createProperty = async (params: CreatePropertyParams): Promise<PropertyType> => {
  const { data } = await axiosInstance.post('/admin/properties', params)
  return data
}

export const updateProperty = async (propertyId: string, params: UpdatePropertyParams): Promise<PropertyType> => {
  const { data } = await axiosInstance.patch(`/admin/properties/${propertyId}`, params)
  return data
}

export const deleteProperty = async (propertyId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/properties/${propertyId}`)

export const bulkDeleteProperties = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/properties', {
    data: params
  })
  return data
}

export const fetchProducts = async (params: ProductsFilterParams, currency?: string): Promise<ProductsListResponse> => {
  const config = currency ? { headers: { 'X-Currency': currency } } : {}
  const { data } = await axiosInstance.get(`/admin/products?${createSearchParams(params).toString()}`, config)
  return data
}

export const fetchProduct = async (productId: string, currency?: string): Promise<ProductType> => {
  const config = currency ? { headers: { 'X-Currency': currency } } : {}
  const { data } = await axiosInstance.get(`/admin/products/${productId}`, config)
  return data
}

export const createProduct = async (params: CreateProductParams): Promise<ProductType> => {
  const { data } = await axiosInstance.post('/admin/products', params)
  return data
}

export const updateProduct = async (productId: string, params: UpdateProductParams): Promise<ProductType> => {
  const { data } = await axiosInstance.patch(`/admin/products/${productId}`, params)
  return data
}

export const deleteProduct = async (productId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/products/${productId}`)

export const bulkDeleteProducts = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/products', {
    data: params
  })
  return data
}

export const fetchProductExcelExports = async (
  params: ProductExcelExportsFilterParams
): Promise<ProductExcelExportsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/product-excel-exports?${createSearchParams(params).toString()}`)
  return data
}

export const fetchProductExcelExport = async (productExcelExportId: string): Promise<ProductExcelExportType> => {
  const { data } = await axiosInstance.get(`/admin/product-excel-exports/${productExcelExportId}`)
  return data
}

export const createProductExcelExport = async (
  params: CreateProductExcelExportParams
): Promise<ProductExcelExportType> => {
  const { data } = await axiosInstance.post('/admin/product-excel-exports', params)
  return data
}

export const deleteProductExcelExport = async (productExcelExportId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/product-excel-exports/${productExcelExportId}`)

export const bulkDeleteProductExcelExports = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/product-excel-exports', {
    data: params
  })
  return data
}

export const fetchProductQrExports = async (
  params: ProductQrExportsFilterParams
): Promise<ProductQrExportsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/product-qr-exports?${createSearchParams(params).toString()}`)
  return data
}

export const fetchProductQrExport = async (productQrExportId: string): Promise<ProductQrExportType> => {
  const { data } = await axiosInstance.get(`/admin/product-qr-exports/${productQrExportId}`)
  return data
}

export const createProductQrExport = async (params: CreateProductQrExportParams): Promise<ProductQrExportType> => {
  const { data } = await axiosInstance.post('/admin/product-qr-exports', params)
  return data
}

export const deleteProductQrExport = async (productQrExportId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/product-qr-exports/${productQrExportId}`)

export const bulkDeleteProductQrExports = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/product-qr-exports', {
    data: params
  })
  return data
}

export const fetchProductPdfExports = async (
  params: ProductPdfExportsFilterParams
): Promise<ProductPdfExportsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/product-pdf-exports?${createSearchParams(params).toString()}`)
  return data
}

export const fetchProductPdfExport = async (productPdfExportId: string): Promise<ProductPdfExportType> => {
  const { data } = await axiosInstance.get(`/admin/product-pdf-exports/${productPdfExportId}`)
  return data
}

export const createProductPdfExport = async (params: CreateProductPdfExportParams): Promise<ProductPdfExportType> => {
  const { data } = await axiosInstance.post('/admin/product-pdf-exports', params)
  return data
}

export const deleteProductPdfExport = async (productPdfExportId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/product-pdf-exports/${productPdfExportId}`)

export const bulkDeleteProductPdfExports = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/product-pdf-exports', {
    data: params
  })
  return data
}

export const fetchProductExcelImports = async (
  params: ProductExcelImportsFilterParams
): Promise<ProductExcelImportsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/product-excel-imports?${createSearchParams(params).toString()}`)
  return data
}

export const fetchProductExcelImport = async (productExcelImportId: string): Promise<ProductExcelImportType> => {
  const { data } = await axiosInstance.get(`/admin/product-excel-imports/${productExcelImportId}`)
  return data
}

export const createProductExcelImport = async (
  params: CreateProductExcelImportParams
): Promise<ProductExcelImportType> => {
  const { data } = await axiosInstance.post('/admin/product-excel-imports', params)
  return data
}

export const retryProductExcelImport = async (productExcelImportId: string): Promise<ProductExcelImportType> => {
  const { data } = await axiosInstance.patch(`/admin/product-excel-imports/${productExcelImportId}`)
  return data
}

export const downloadProductExcelImportTemplate = async (): Promise<{ blob: Blob; filename: string | null }> => {
  const response = await axiosInstance.get('/admin/product-excel-imports/template', {
    responseType: 'blob'
  })

  // Extract filename from the Content-Disposition header
  const contentDisposition = response.headers['content-disposition']
  let filename: string | null = null

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '')
    }
  }

  return { blob: response.data, filename }
}

export const deleteProductExcelImport = async (productExcelImportId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/product-excel-imports/${productExcelImportId}`)

export const bulkDeleteProductExcelImports = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/product-excel-imports', {
    data: params
  })
  return data
}

export const fetchProductPictureImports = async (
  params: ProductPictureImportsFilterParams
): Promise<ProductPictureImportsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/product-picture-imports?${createSearchParams(params).toString()}`)
  return data
}

export const fetchProductPictureImport = async (productPictureImportId: string): Promise<ProductPictureImportType> => {
  const { data } = await axiosInstance.get(`/admin/product-picture-imports/${productPictureImportId}`)
  return data
}

export const createProductPictureImport = async (
  params: CreateProductPictureImportParams
): Promise<ProductPictureImportType> => {
  const { data } = await axiosInstance.post('/admin/product-picture-imports', params)
  return data
}

export const retryProductPictureImport = async (productPictureImportId: string): Promise<ProductPictureImportType> => {
  const { data } = await axiosInstance.patch(`/admin/product-picture-imports/${productPictureImportId}`)
  return data
}

export const deleteProductPictureImport = async (productPictureImportId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/product-picture-imports/${productPictureImportId}`)

export const bulkDeleteProductPictureImports = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/product-picture-imports', {
    data: params
  })
  return data
}

export const fetchBlogPosts = async (params: BlogPostsFilterParams): Promise<BlogPostsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/blog-posts?${createSearchParams(params).toString()}`)
  return data
}

export const fetchBlogPost = async (blogPostId: string): Promise<BlogPostType> => {
  const { data } = await axiosInstance.get(`/admin/blog-posts/${blogPostId}`)
  return data
}

export const createBlogPost = async (params: CreateBlogPostParams): Promise<BlogPostType> => {
  const { data } = await axiosInstance.post('/admin/blog-posts', params)
  return data
}

export const updateBlogPost = async (blogPostId: string, params: UpdateBlogPostParams): Promise<BlogPostType> => {
  const { data } = await axiosInstance.patch(`/admin/blog-posts/${blogPostId}`, params)
  return data
}

export const deleteBlogPost = async (blogPostId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/blog-posts/${blogPostId}`)

export const bulkDeleteBlogPosts = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/blog-posts', {
    data: params
  })
  return data
}

export const fetchPages = async (params: PagesFilterParams): Promise<PagesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/pages?${createSearchParams(params).toString()}`)
  return data
}

export const fetchPage = async (pageId: string): Promise<PageType> => {
  const { data } = await axiosInstance.get(`/admin/pages/${pageId}`)
  return data
}

export const createPage = async (params: CreatePageParams): Promise<PageType> => {
  const { data } = await axiosInstance.post('/admin/pages', params)
  return data
}

export const updatePage = async (pageId: string, params: UpdatePageParams): Promise<PageType> => {
  const { data } = await axiosInstance.patch(`/admin/pages/${pageId}`, params)
  return data
}

export const deletePage = async (pageId: string): Promise<void> => await axiosInstance.delete(`/admin/pages/${pageId}`)

export const sortPage = async (pageId: string, type: SortOrderEnum): Promise<PageType> => {
  const { data } = await axiosInstance.patch(`/admin/pages/${pageId}/${type}`)
  return data
}

export const bulkDeletePages = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/pages', {
    data: params
  })
  return data
}

export const fetchProductionStages = async (
  params: ProductionStagesFilterParams
): Promise<ProductionStagesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/production-stages?${createSearchParams(params).toString()}`)
  return data
}

export const fetchProductionStage = async (productionStageId: string): Promise<ProductionStageType> => {
  const { data } = await axiosInstance.get(`/admin/production-stages/${productionStageId}`)
  return data
}

export const createProductionStage = async (params: CreateProductionStageParams): Promise<ProductionStageType> => {
  const { data } = await axiosInstance.post('/admin/production-stages', params)
  return data
}

export const updateProductionStage = async (
  productionStageId: string,
  params: UpdateProductionStageParams
): Promise<ProductionStageType> => {
  const { data } = await axiosInstance.patch(`/admin/production-stages/${productionStageId}`, params)
  return data
}

export const deleteProductionStage = async (productionStageId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/production-stages/${productionStageId}`)

export const sortProductionStage = async (
  productionStageId: string,
  type: SortOrderEnum
): Promise<ProductionStageType> => {
  const { data } = await axiosInstance.patch(`/admin/production-stages/${productionStageId}/${type}`)
  return data
}

export const bulkDeleteProductionStages = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/production-stages', {
    data: params
  })
  return data
}

export const fetchPhotoGalleries = async (params: PhotoGalleriesFilterParams): Promise<PhotoGalleriesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/photo-galleries?${createSearchParams(params).toString()}`)
  return data
}

export const fetchPhotoGallery = async (photoGalleryId: string): Promise<PhotoGalleryType> => {
  const { data } = await axiosInstance.get(`/admin/photo-galleries/${photoGalleryId}`)
  return data
}

export const createPhotoGallery = async (params: CreatePhotoGalleryParams): Promise<PhotoGalleryType> => {
  const { data } = await axiosInstance.post('/admin/photo-galleries', params)
  return data
}

export const updatePhotoGallery = async (
  photoGalleryId: string,
  params: UpdatePhotoGalleryParams
): Promise<PhotoGalleryType> => {
  const { data } = await axiosInstance.patch(`/admin/photo-galleries/${photoGalleryId}`, params)
  return data
}

export const sortPhotoGallery = async (photoGalleryId: string, type: SortOrderEnum): Promise<PhotoGalleryType> => {
  const { data } = await axiosInstance.patch(`/admin/photo-galleries/${photoGalleryId}/${type}`)
  return data
}

export const deletePhotoGallery = async (photoGalleryId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/photo-galleries/${photoGalleryId}`)

export const bulkDeletePhotoGalleries = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/photo-galleries', {
    data: params
  })
  return data
}

export const fetchVideos = async (params: VideosFilterParams): Promise<VideosListResponse> => {
  const { data } = await axiosInstance.get(`/admin/videos?${createSearchParams(params).toString()}`)
  return data
}

export const fetchVideo = async (photoGalleryId: string): Promise<VideoType> => {
  const { data } = await axiosInstance.get(`/admin/videos/${photoGalleryId}`)
  return data
}

export const createVideo = async (params: CreateVideoParams): Promise<VideoType> => {
  const { data } = await axiosInstance.post('/admin/videos', params)
  return data
}

export const updateVideo = async (photoGalleryId: string, params: UpdateVideoParams): Promise<VideoType> => {
  const { data } = await axiosInstance.patch(`/admin/videos/${photoGalleryId}`, params)
  return data
}

export const sortVideo = async (photoGalleryId: string, type: SortOrderEnum): Promise<VideoType> => {
  const { data } = await axiosInstance.patch(`/admin/videos/${photoGalleryId}/${type}`)
  return data
}

export const deleteVideo = async (photoGalleryId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/videos/${photoGalleryId}`)

export const bulkDeleteVideos = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/videos', {
    data: params
  })
  return data
}

export const fetchSlides = async (params: SlidesFilterParams): Promise<SlidesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/slides?${createSearchParams(params).toString()}`)
  return data
}

export const fetchSlide = async (photoGalleryId: string): Promise<SlideType> => {
  const { data } = await axiosInstance.get(`/admin/slides/${photoGalleryId}`)
  return data
}

export const createSlide = async (params: CreateSlideParams): Promise<SlideType> => {
  const { data } = await axiosInstance.post('/admin/slides', params)
  return data
}

export const updateSlide = async (photoGalleryId: string, params: UpdateSlideParams): Promise<SlideType> => {
  const { data } = await axiosInstance.patch(`/admin/slides/${photoGalleryId}`, params)
  return data
}

export const sortSlide = async (photoGalleryId: string, type: SortOrderEnum): Promise<SlideType> => {
  const { data } = await axiosInstance.patch(`/admin/slides/${photoGalleryId}/${type}`)
  return data
}

export const deleteSlide = async (photoGalleryId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/slides/${photoGalleryId}`)

export const bulkDeleteSlides = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/slides', {
    data: params
  })
  return data
}

export const fetchCatalogues = async (params: CataloguesFilterParams): Promise<CataloguesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/catalogues?${createSearchParams(params).toString()}`)
  return data
}

export const fetchCatalogue = async (catalogueId: string): Promise<CatalogueType> => {
  const { data } = await axiosInstance.get(`/admin/catalogues/${catalogueId}`)
  return data
}

export const createCatalogue = async (params: CreateCatalogueParams): Promise<CatalogueType> => {
  const { data } = await axiosInstance.post('/admin/catalogues', params)
  return data
}

export const updateCatalogue = async (catalogueId: string, params: UpdateCatalogueParams): Promise<CatalogueType> => {
  const { data } = await axiosInstance.patch(`/admin/catalogues/${catalogueId}`, params)
  return data
}

export const sortCatalogue = async (catalogueId: string, type: SortOrderEnum): Promise<CatalogueType> => {
  const { data } = await axiosInstance.patch(`/admin/catalogues/${catalogueId}/${type}`)
  return data
}

export const deleteCatalogue = async (catalogueId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/catalogues/${catalogueId}`)

export const bulkDeleteCatalogues = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/catalogues', {
    data: params
  })
  return data
}

export const fetchCertificates = async (params: CertificatesFilterParams): Promise<CertificatesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/certificates?${createSearchParams(params).toString()}`)
  return data
}

export const fetchCertificate = async (certificateId: string): Promise<CertificateType> => {
  const { data } = await axiosInstance.get(`/admin/certificates/${certificateId}`)
  return data
}

export const createCertificate = async (params: CreateCertificateParams): Promise<CertificateType> => {
  const { data } = await axiosInstance.post('/admin/certificates', params)
  return data
}

export const updateCertificate = async (
  certificateId: string,
  params: UpdateCertificateParams
): Promise<CertificateType> => {
  const { data } = await axiosInstance.patch(`/admin/certificates/${certificateId}`, params)
  return data
}

export const sortCertificate = async (certificateId: string, type: SortOrderEnum): Promise<CertificateType> => {
  const { data } = await axiosInstance.patch(`/admin/certificates/${certificateId}/${type}`)
  return data
}

export const deleteCertificate = async (certificateId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/certificates/${certificateId}`)

export const bulkDeleteCertificates = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/certificates', {
    data: params
  })
  return data
}

export const fetchEvents = async (params: EventsFilterParams): Promise<EventsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/events?${createSearchParams(params).toString()}`)
  return data
}

export const fetchEvent = async (eventId: string): Promise<EventType> => {
  const { data } = await axiosInstance.get(`/admin/events/${eventId}`)
  return data
}

export const createEvent = async (params: CreateEventParams): Promise<EventType> => {
  const { data } = await axiosInstance.post('/admin/events', params)
  return data
}

export const updateEvent = async (eventId: string, params: UpdateEventParams): Promise<EventType> => {
  const { data } = await axiosInstance.patch(`/admin/events/${eventId}`, params)
  return data
}

export const deleteEvent = async (eventId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/events/${eventId}`)

export const bulkDeleteEvents = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/events', {
    data: params
  })
  return data
}

// Groups API functions
export const fetchPermissions = async (params?: PermissionsFilterParams): Promise<PermissionsListResponse> => {
  const queryString = params ? `?${createSearchParams(params).toString()}` : ''
  const { data } = await axiosInstance.get(`/admin/permissions${queryString}`)
  return data
}

export const fetchGroups = async (params: GroupsFilterParams): Promise<GroupsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/groups?${createSearchParams(params).toString()}`)
  return data
}

export const fetchGroup = async (groupId: string): Promise<GroupType> => {
  const { data } = await axiosInstance.get(`/admin/groups/${groupId}`)
  return data
}

export const createGroup = async (params: CreateGroupParams): Promise<GroupType> => {
  const { data } = await axiosInstance.post('/admin/groups', params)
  return data
}

export const updateGroup = async (groupId: string, params: UpdateGroupParams): Promise<GroupType> => {
  const { data } = await axiosInstance.patch(`/admin/groups/${groupId}`, params)
  return data
}

export const deleteGroup = async (groupId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/groups/${groupId}`)

export const bulkDeleteGroups = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/groups', {
    data: params
  })
  return data
}

export const fetchNotifications = async (
  params: NotificationsFilterParams = {}
): Promise<NotificationsListResponse> => {
  const { data } = await axiosInstance.get(`/notifications?${createSearchParams(params).toString()}`)
  return data
}

export const setReadNotifications = async (params: SetReadNotificationParams): Promise<NotificationType> => {
  const { data } = await axiosInstance.patch(`/notifications`, params)
  return data
}

export const deleteNotification = async (notificationId: string): Promise<void> =>
  await axiosInstance.delete(`/notifications/${notificationId}`)

export const bulkDeleteNotifications = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/notifications', {
    data: params
  })
  return data
}

// Contact Units
export const fetchContactUnits = async (params: ContactUnitsFilterParams): Promise<ContactUnitsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/contact-units?${createSearchParams(params).toString()}`)
  return data
}

export const fetchContactUnit = async (contactUnitId: string): Promise<ContactUnitType> => {
  const { data } = await axiosInstance.get(`/admin/contact-units/${contactUnitId}`)
  return data
}

export const createContactUnit = async (params: CreateContactUnitParams): Promise<ContactUnitType> => {
  const { data } = await axiosInstance.post('/admin/contact-units', params)
  return data
}

export const updateContactUnit = async (
  contactUnitId: string,
  params: UpdateContactUnitParams
): Promise<ContactUnitType> => {
  const { data } = await axiosInstance.patch(`/admin/contact-units/${contactUnitId}`, params)
  return data
}

export const sortContactUnit = async (contactUnitId: string, type: SortOrderEnum): Promise<ContactUnitType> => {
  const { data } = await axiosInstance.patch(`/admin/contact-units/${contactUnitId}/${type}`)
  return data
}

export const deleteContactUnit = async (contactUnitId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/contact-units/${contactUnitId}`)

export const bulkDeleteContactUnits = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/contact-units', {
    data: params
  })
  return data
}

// Audit Logs
export const fetchAuditLogs = async (params: AuditLogsFilterParams): Promise<AuditLogsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/audit-logs?${createSearchParams(params).toString()}`)
  return data
}

export const fetchAuditLog = async (auditLogId: string): Promise<AuditLogType> => {
  const { data } = await axiosInstance.get(`/admin/audit-logs/${auditLogId}`)
  return data
}

export const deleteAuditLog = async (auditLogId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/audit-logs/${auditLogId}`)

export const bulkDeleteAuditLogs = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/audit-logs', {
    data: params
  })
  return data
}

export const exportAuditLogs = async (
  params: AuditLogsFilterParams
): Promise<{ blob: Blob; filename: string | null }> => {
  const { page, size, ...exportParams } = params
  void page
  void size
  const response = await axiosInstance.get(`/admin/audit-logs/export?${createSearchParams(exportParams).toString()}`, {
    responseType: 'blob'
  })

  const contentDisposition = response.headers['content-disposition']
  let filename: string | null = null

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '')
    }
  }

  return { blob: response.data, filename }
}

// Backup Schedules
export const fetchBackups = async (params: BackupsFilterParams): Promise<BackupsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/backup-schedules?${createSearchParams(params).toString()}`)
  return data
}

export const fetchBackup = async (id: string): Promise<BackupScheduleType> => {
  const { data } = await axiosInstance.get(`/admin/backup-schedules/${id}`)
  return data
}

export const createBackupSchedule = async (params: CreateBackupScheduleParams): Promise<BackupScheduleType> => {
  const { data } = await axiosInstance.post('/admin/backup-schedules', params)
  return data
}

export const updateBackupSchedule = async (
  id: string,
  params: UpdateBackupScheduleParams
): Promise<BackupScheduleType> => {
  const { data } = await axiosInstance.patch(`/admin/backup-schedules/${id}`, params)
  return data
}

export const deleteBackupSchedule = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/admin/backup-schedules/${id}`)
}

export const bulkDeleteBackupSchedules = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/backup-schedules', { data: params })
  return data
}

export const runBackupSchedule = async (id: string): Promise<void> => {
  await axiosInstance.post(`/admin/backup-schedules/${id}/run`)
}

export const triggerBackup = async (params: CreateBackupParams): Promise<BackupType> => {
  const { data } = await axiosInstance.post('/admin/backups/trigger', params)
  return data
}

// Backup Runs
export const fetchBackupRuns = async (params: BackupRunsFilterParams): Promise<BackupRunsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/backups?${createSearchParams(params).toString()}`)
  return data
}

export const fetchBackupRun = async (id: string): Promise<BackupType> => {
  const { data } = await axiosInstance.get(`/admin/backups/${id}`)
  return data
}

export const deleteBackupRun = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/admin/backups/${id}`)
}

export const bulkDeleteBackupRuns = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/backups', { data: params })
  return data
}

// Contact Form Messages
export const fetchContactFormMessages = async (
  params: ContactFormMessagesFilterParams
): Promise<ContactFormMessagesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/contact-form-messages?${createSearchParams(params).toString()}`)
  return data
}

export const fetchContactFormMessage = async (id: string): Promise<ContactFormMessageType> => {
  const { data } = await axiosInstance.get(`/admin/contact-form-messages/${id}`)
  return data
}

export const deleteContactFormMessage = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/admin/contact-form-messages/${id}`)
}

export const bulkDeleteContactFormMessages = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/contact-form-messages', { data: params })
  return data
}

export const fetchJobApplications = async (
  params: JobApplicationsFilterParams
): Promise<JobApplicationsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/job-applications?${createSearchParams(params).toString()}`)
  return data
}

export const fetchJobApplication = async (id: string): Promise<JobApplicationType> => {
  const { data } = await axiosInstance.get(`/admin/job-applications/${id}`)
  return data
}

export const deleteJobApplication = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/admin/job-applications/${id}`)
}

export const bulkDeleteJobApplications = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/job-applications', { data: params })
  return data
}

export const downloadJobApplicationPdf = async (id: string): Promise<{ blob: Blob; filename: string | null }> => {
  const response = await axiosInstance.get(`/admin/job-applications/${id}/pdf`, {
    responseType: 'blob'
  })

  const contentDisposition = response.headers['content-disposition']
  let filename: string | null = null

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '')
    }
  }

  return { blob: response.data, filename }
}

export const fetchCurrencies = async (params: CurrenciesFilterParams = {}): Promise<CurrenciesListResponse> => {
  const { data } = await axiosInstance.get(`/admin/currencies?${createSearchParams(params).toString()}`)
  return data
}

export const fetchCurrency = async (id: string): Promise<CurrencyType> => {
  const { data } = await axiosInstance.get(`/admin/currencies/${id}`)
  return data
}

export const createCurrency = async (params: CreateCurrencyParams): Promise<CurrencyType> => {
  const { data } = await axiosInstance.post('/admin/currencies', params)
  return data
}

export const updateCurrency = async (id: string, params: UpdateCurrencyParams): Promise<CurrencyType> => {
  const { data } = await axiosInstance.put(`/admin/currencies/${id}`, params)
  return data
}

export const deleteCurrency = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/admin/currencies/${id}`)
}

export const bulkDeleteCurrencies = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/currencies', { data: params })
  return data
}

export const sortCurrency = async (id: string, type: SortOrderEnum): Promise<CurrencyType> => {
  const { data } = await axiosInstance.patch(`/admin/currencies/${id}/${type}`)
  return data
}

export const fetchExchangeRates = async (
  params: ExchangeRatesFilterParams = {}
): Promise<ExchangeRatesListResponse> => {
  const { data } = await axiosInstance.get('/admin/exchange-rates', { params: createSearchParams(params) })
  return data
}

export const syncExchangeRates = async (): Promise<ExchangeRateSyncResponse> => {
  const { data } = await axiosInstance.post('/admin/exchange-rates/sync')
  return data
}

export const fetchSearchLogs = async (params: SearchLogsFilterParams): Promise<SearchLogsListResponse> => {
  const { data } = await axiosInstance.get(`/admin/search-logs?${createSearchParams(params).toString()}`)
  return data
}

export const fetchSearchLog = async (searchLogId: string): Promise<SearchLogType> => {
  const { data } = await axiosInstance.get(`/admin/search-logs/${searchLogId}`)
  return data
}

export const deleteSearchLog = async (searchLogId: string): Promise<void> =>
  await axiosInstance.delete(`/admin/search-logs/${searchLogId}`)

export const bulkDeleteSearchLogs = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/search-logs', { data: params })
  return data
}

export const fetchOrders = async (params: OrdersFilterParams): Promise<OrdersListResponse> => {
  const { data } = await axiosInstance.get(`/admin/orders?${createSearchParams(params).toString()}`)
  return data
}

export const fetchOrder = async (id: string): Promise<OrderType> => {
  const { data } = await axiosInstance.get(`/admin/orders/${id}`)
  return data
}

export const updateOrder = async (id: string, params: UpdateOrderParams): Promise<OrderType> => {
  const { data } = await axiosInstance.patch(`/admin/orders/${id}`, params)
  return data
}

export const updateOrderStatus = async (id: string, params: UpdateOrderStatusParams): Promise<OrderType> => {
  const { data } = await axiosInstance.patch(`/admin/orders/${id}/status`, params)
  return data
}

export const deleteOrder = async (id: string): Promise<void> => await axiosInstance.delete(`/admin/orders/${id}`)

export const bulkDeleteOrders = async (params: BulkIdParams): Promise<BulkDeleteResponse> => {
  const { data } = await axiosInstance.delete('/admin/orders', {
    data: params
  })
  return data
}

export const createGuestOrder = async (params: AdminCreateGuestOrderParams, currency?: string): Promise<OrderType> => {
  const config = currency ? { headers: { 'X-Currency': currency } } : {}
  const { data } = await axiosInstance.post('/admin/orders/guest', params, config)
  return data
}

export const createRegisteredOrder = async (
  params: AdminCreateRegisteredOrderParams,
  currency?: string
): Promise<OrderType> => {
  const config = currency ? { headers: { 'X-Currency': currency } } : {}
  const { data } = await axiosInstance.post('/admin/orders/registered', params, config)
  return data
}

export const addOrderItem = async (orderId: string, params: AddOrderItemParams): Promise<OrderType> => {
  const { data } = await axiosInstance.post(`/admin/orders/${orderId}/items`, params)
  return data
}

export const updateOrderItem = async (
  orderId: string,
  itemId: string,
  params: UpdateOrderItemParams
): Promise<OrderType> => {
  const { data } = await axiosInstance.patch(`/admin/orders/${orderId}/items/${itemId}`, params)
  return data
}

export const removeOrderItem = async (orderId: string, itemId: string): Promise<OrderType> => {
  const { data } = await axiosInstance.delete(`/admin/orders/${orderId}/items/${itemId}`)
  return data
}

export const addOrderNote = async (orderId: string, params: OrderNoteParams): Promise<OrderNoteType> => {
  const { data } = await axiosInstance.post(`/admin/orders/${orderId}/notes`, params)
  return data
}

export const updateOrderNote = async (
  orderId: string,
  noteId: string,
  params: OrderNoteParams
): Promise<OrderNoteType> => {
  const { data } = await axiosInstance.patch(`/admin/orders/${orderId}/notes/${noteId}`, params)
  return data
}

export const deleteOrderNote = async (orderId: string, noteId: string): Promise<void> => {
  await axiosInstance.delete(`/admin/orders/${orderId}/notes/${noteId}`)
}

export const downloadOrderExport = async (
  id: string,
  type: 'excel' | 'pdf',
  locale: string
): Promise<{ blob: Blob; filename: string | null }> => {
  const response = await axiosInstance.get(`/admin/orders/${id}/export/${type}`, {
    responseType: 'blob',
    headers: { 'Accept-Language': locale }
  })

  const contentDisposition = response.headers['content-disposition']
  let filename: string | null = null
  if (contentDisposition) {
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
    if (match && match[1]) {
      filename = match[1].replace(/['"]/g, '')
    }
  }

  return { blob: response.data, filename }
}
